import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { FormSchema, GeneratedFile, GeneratorOptions } from '@cli-types/core-engine/schema.js'
import type { FormPipelineResult } from '@cli-types/next-adapter/forms.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths/resolve-extension-namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { generateFormAdminPages } from '@next-adapter/generators/form-admin/orchestrator.js'
import { updateFormNavigation } from '@next-adapter/generators/form-navigation.js'
import { namespaceGeneratedFiles } from '@next-adapter/generators/namespace-output/namespace-generated-files.js'
import { updateFormWebhookEvents } from '@next-adapter/generators/webhook-events/update-form-webhook-events.js'
import { generateFormActions } from './form-actions/generate-form-actions.js'
import { generateFormComponent } from './form-component.js'
import { generateFormDatabase } from './form-database.js'
import { generateFormHook } from './form-hook.js'

export function runFormPipeline(
  schema: FormSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: GeneratorOptions = {}
): FormPipelineResult {
  const paths = resolveProjectPaths(config)
  const outputNamespace = resolveExtensionNamespace(schema.name, options.outputNamespace)
  const adminNamespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const namespacedOptions = {
    ...options,
    outputNamespace,
    adminDir: paths.adminDir,
    adminRoutePath: adminNamespace.routePath,
    adminImportAlias: adminNamespace.alias,
    nextMajorVersion: config.nextMajorVersion
  }
  const files: GeneratedFile[] = []
  const errors: string[] = []

  const steps: { name: string; run: () => GeneratedFile[] }[] = [
    {
      name: 'Database schema (submissions)',
      run: () => generateFormDatabase(schema, paths.adminDbDir, namespacedOptions).files
    },
    {
      name: 'Server actions',
      run: () => generateFormActions(schema, paths.adminActionsDir, namespacedOptions).files
    },
    {
      name: 'React Query hook',
      run: () => generateFormHook(schema, paths.adminHooksDir, namespacedOptions).files
    },
    {
      name: 'Public form component',
      run: () => generateFormComponent(schema, cwd, paths.adminDir, namespacedOptions).files
    },
    {
      name: 'Admin pages',
      run: () => generateFormAdminPages(schema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Navigation',
      run: () => updateFormNavigation(schema, paths.adminNavigationDir, namespacedOptions).files
    },
    {
      name: 'Webhook events',
      run: () => updateFormWebhookEvents(schema, paths.adminWebhookEventsDir).files
    }
  ]

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepNum = i + 1
    try {
      const result = step.run()
      files.push(...result)
      if (!options.silent) console.log(`   ${stepNum}. ${step.name} ✓`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      errors.push(`${step.name}: ${msg}`)
      if (!options.silent) console.error(`   ${stepNum}. ${step.name} ✗ — ${msg}`)
    }
  }

  return {
    success: errors.length === 0,
    files: namespaceGeneratedFiles(files, config.frameworkConfig.next.namespace),
    errors
  }
}
